<template>
    <div id="contactsMap">
        <div class="contacts-block__map--message" v-bind:class="{'messageOverlay': disableMapInteraction}">{{mobileScrollMessage}}</div>
    </div>
</template>

<script>
    import { markRaw } from 'vue';

    export default {
        props: {
            markers: { type: Array, default: () => [] },
            selectedMapItem: { type: Object, default: null },
            companyType: { type: Number, default: null },
            companyTypesEnum: { type: Array, default: () => [] },
            clientId: { type: String, default: '' },
            guid: { type: String, default: '' },
            isMapShown: { type: Boolean, default: true },
            mobileScrollMessage: { type: String, default: '' }
        },
        data: function() {
            return {
                map: null,
                datasource: null,
                companyTypes: [],
                companyTypesColors: [],
                selectedOffice: this.selectedMapItem,
                popup: null,
                coordinates: [],
                disableMapInteraction: false,
                singleMarkerZoom: 15,
                clusterMaxZoom: 13,
                clusterProperties: {},
                data: [],
                mapInitialized: false
            }
        },
        mounted() {
            var guid = this.guid;
            //Initialize a map instance.
            this.map = markRaw(new atlas.Map('contactsMap', {
                center: this.selectedOffice ? [this.selectedOffice.longitude, this.selectedOffice.latitude] : [0,0],
                zoom: this.selectedOffice ? 7 : 1,
                language: 'en-US',
                authOptions: {
                    authType: "anonymous",
                    clientId: this.clientId,
                    getToken: function (resolve, reject, map) {
                        fetch(window.location.origin + '/api/contacts/gettoken/?guid=' + guid).then(function (response) {
                            return response.text();
                        }).then(function (token) {
                            resolve(token);
                        });
                    }
                }
                }));
            if (this.companyTypesEnum.length > 0 || this.selectedOffice) {
                this.initializeMap();
            }
        },
        watch: {
            companyTypesEnum: function (newVal) {
                // Initialize map only once and if companyTypes are available
                if (!this.mapInitialized && newVal.length > 0) {
                    this.initializeMap();
                }
            },
            selectedMapItem: function (newVal, oldVal) {
                this.selectedOffice = this.selectedMapItem;
                if (newVal && this.datasource) {
                    this.datasource.setShapes(this.data);
                    this.$nextTick(() => {
                        this.map.resize();
                        this.zoomToSingleMarker([newVal.longitude, newVal.latitude]);
                    }); 
                }
                else if (oldVal && !newVal) {
                    this.datasource.setShapes(this.data);
                    this.$nextTick(() => {
                        this.map.resize();
                    }); 
                }
                else {
                    this.updateData();
                }
            },
            markers: function (newVal, oldVal) {
                if (newVal) {
                    this.updateData();
                }
            },
            isMapShown: function (newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.$nextTick(() => {
                        this.map.resize();
                    }); 
                    this.updateData();
                }
            }
        },
        methods: { 
            initializeMap: function() {
                //Wait until the map resources are ready.
                this.map.events.add('ready', () => {
                    //Create a reusable popup.
                    this.popup = new atlas.Popup({
                        pixelOffset: [0, -50],
                        closeButton: false
                    });

                    this.map.resize();

                    var zoomControl = new atlas.control.ZoomControl();
                    this.map.controls.add(zoomControl, {
                        position: "bottom-right"
                    });

                    const vm = this;
                    vm.companyTypesEnum.forEach(item => {
                        vm.companyTypes.push(item.name);
                        if (item.value === 0) { // Metso
                            vm.companyTypesColors.push('#eb2814');
                            vm.clusterProperties[item.name] = ['+', ['case', ['==', ['get', 'companyType'], 0], 1, 0]];
                        }
                        else if (item.value === 1) { // Distributors
                            vm.companyTypesColors.push('#000');
                            vm.clusterProperties[item.name] = ['+', ['case', ['==', ['get', 'companyType'], 1], 1, 0]];
                        }
                    });
                    vm.datasource = new atlas.source.DataSource(null, {
                        //Tell the data source to cluster point data.
                        cluster: true,

                        //The radius in pixels to cluster points together.
                        clusterRadius: 45,

                        clusterMaxZoom: vm.clusterMaxZoom,

                        clusterProperties: vm.clusterProperties
                    });
                    vm.map.sources.add(vm.datasource);

                    //Create a HTML marker layer for rendering data points.
                    var markerLayer = new atlas.layer.HtmlMarkerLayer(vm.datasource, null, {
                        markerCallback: function (id, position, properties) {
                            //Check to see if marker represents a cluster.
                            if (properties.cluster) {
                                var radius = 20;

                                if (properties.point_count > 1000) {
                                    radius = 50;
                                } else if (properties.point_count > 100) {
                                    radius = 40;
                                } else if (properties.point_count > 10) {
                                    radius = 30;
                                } else if (properties.point_count == 2) {
                                    radius = 20;
                                }

                                //Get the counts for each entity type in the cluster.
                                var values = [0, 0];

                                for (var i = 0; i < vm.companyTypes.length; i++) {
                                    if (properties[vm.companyTypes[i]]) {
                                        values[i] = properties[vm.companyTypes[i]];
                                    }
                                }

                                return new atlas.PieChartMarker({
                                    position: position,
                                    values: values,
                                    colors: vm.companyTypesColors,
                                    radius: radius,
                                    fillColor: 'white',
                                    strokeWidth: 1,
                                    strokeColor: 'white',
                                    innerRadius: radius * 0.5,
                                    text: properties.point_count_abbreviated,
                                    tooltipCallback: vm.tooltipCallback
                                });
                            } else {
                                //Individual points
                                var color,
                                    htmlContent;

                                switch (properties.companyType) {
                                    case 0: // Office location
                                        color = vm.companyTypesColors[0];
                                        if (vm.selectedOffice && vm.selectedOffice.id === properties.id) {
                                            htmlContent = '<svg width="36" height="56" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path d="M17.652 0C7.902 0 0 8.01 0 17.892c0 4.262 1.478 8.169 3.935 11.24.792.989 2.116 2.545 2.807 3.61 3.976 6.122 9.076 15.013 10.563 22.31.255 1.246.562 1.268.893.04 1.06-3.934 3.81-12.53 9.489-21.526.677-1.072 2.05-2.574 2.902-3.508a18.07 18.07 0 0 0 3.042-4.598 17.997 17.997 0 0 0 1.673-7.566C35.304 8.011 27.402 0 17.652 0Zm0 27.56c-5.442 0-9.853-4.472-9.853-9.988 0-5.515 4.411-9.986 9.853-9.986s9.853 4.472 9.853 9.986c0 5.516-4.41 9.987-9.853 9.987Z" fill="#E5097C" fill-rule="nonzero"/><circle fill="#E5097C" fill-rule="nonzero" cx="17.5" cy="17.5" r="11.5"/><g fill="#FFF"><path d="M16 15.04h1v-1h-1zM16 17.039h1v-1.001h-1zM18 15.04h1v-1h-1zM18 17.039h1v-1.001h-1z"/><path d="M15 10v2.002h-1v5.999h-1V24h-2v.999h13V24h-2v-5.999h-1.009v-5.999H20V10h-5Zm1.001 2.002h3v-1.001h-3v1.001ZM15 18.001h4.991v-4.999H15v4.999ZM14 24h7v-4.998h-7V24Z"/><path d="M15 21.04h1.001v-1H15zM15 23.039h1.001v-1H15zM17 21.04h1v-1h-1zM17 23.039h1v-1h-1zM19 21.04h1v-1h-1zM19 23.039h1v-1h-1z"/></g></g></svg>';
                                        }
                                        else {
                                            htmlContent = '<svg width="29" height="46" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path d="M14.5 0C6.491 0 0 6.58 0 14.697c0 3.5 1.214 6.71 3.232 9.233.65.812 1.739 2.09 2.306 2.965 3.266 5.029 7.456 12.333 8.677 18.326.21 1.024.462 1.042.733.034.871-3.232 3.13-10.294 7.795-17.683.556-.88 1.683-2.114 2.384-2.882a14.843 14.843 0 0 0 2.499-3.777A14.784 14.784 0 0 0 29 14.7C29 6.58 22.509 0 14.5 0Zm0 22.638c-4.47 0-8.094-3.673-8.094-8.204 0-4.53 3.624-8.202 8.094-8.202 4.47 0 8.094 3.672 8.094 8.202 0 4.531-3.624 8.204-8.094 8.204Z" fill="#EB2814" fill-rule="nonzero"/><circle fill="#EB2814" fill-rule="nonzero" cx="14" cy="14" r="9"/><g fill="#090909"><path d="M13 12.04h1v-1h-1zM13 14.039h1v-1.001h-1zM15 12.04h1v-1h-1zM15 14.039h1v-1.001h-1z"/><path d="M12 7v2.002h-1v5.999h-1V21H8v.999h13V21h-2v-5.999h-1.009V9.002H17V7h-5Zm1.001 2.002h3V8.001h-3v1.001ZM12 15.001h4.991v-4.999H12v4.999ZM11 21h7v-4.998h-7V21Z"/><path d="M12 18.04h1.001v-1H12zM12 20.039h1.001v-1H12zM14 18.04h1v-1h-1zM14 20.039h1v-1h-1zM16 18.04h1v-1h-1zM16 20.039h1v-1h-1z"/></g></g></svg>';
                                        }

                                        break;
                                    case 1: // Distributor
                                        color = vm.companyTypesColors[1];
                                        if (vm.selectedOffice && vm.selectedOffice.id === properties.id) {
                                            htmlContent = '<svg width="36" height="56" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path d="M17.652 0C7.902 0 0 8.01 0 17.892c0 4.262 1.478 8.169 3.935 11.24.792.989 2.116 2.545 2.807 3.61 3.976 6.122 9.076 15.013 10.563 22.31.255 1.246.562 1.268.893.04 1.06-3.934 3.81-12.53 9.489-21.526.677-1.072 2.05-2.574 2.902-3.508a18.07 18.07 0 0 0 3.042-4.598 17.997 17.997 0 0 0 1.673-7.566C35.304 8.011 27.402 0 17.652 0Zm0 27.56c-5.442 0-9.853-4.472-9.853-9.988 0-5.515 4.411-9.986 9.853-9.986s9.853 4.472 9.853 9.986c0 5.516-4.41 9.987-9.853 9.987Z" fill="#E5097C" fill-rule="nonzero"/><circle fill="#E5097C" fill-rule="nonzero" cx="17.5" cy="17.5" r="11.5"/><g fill="#FFF"><path d="m20.445 20.178 4.37 4.47-.715.699-2.208-2.259-1.413.831-1.443-.847-.694.718-.519-.902 2.622-2.71Zm.004 1.434-.698.72.728.427.693-.407-.723-.74Z"/><path d="m23.486 11-2.399 3.001L23.486 17h-8.54v.877l3.945 6.87-.867.497-2.157-3.754-1.388 1.39-1.429-1.429-2.182 3.798-.868-.498 3.946-6.87V11h9.54Zm-9.04 8.017-.881 1.535.914.914.873-.874-.906-1.575ZM21.406 12h-6.46v4h6.46l-1.601-1.999L21.406 12Z"/></g></g></svg>';
                                        }
                                        else {
                                            htmlContent = '<svg width="29" height="46" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path d="M14.5 0C6.491 0 0 6.58 0 14.697c0 3.5 1.214 6.71 3.232 9.233.65.812 1.739 2.09 2.306 2.965 3.266 5.029 7.456 12.333 8.677 18.326.21 1.024.462 1.042.733.034.871-3.232 3.13-10.294 7.795-17.683.556-.88 1.683-2.114 2.384-2.882a14.843 14.843 0 0 0 2.499-3.777A14.784 14.784 0 0 0 29 14.7C29 6.58 22.509 0 14.5 0Zm0 22.638c-4.47 0-8.094-3.673-8.094-8.204 0-4.53 3.624-8.202 8.094-8.202 4.47 0 8.094 3.672 8.094 8.202 0 4.531-3.624 8.204-8.094 8.204Z" fill="#090909" fill-rule="nonzero"/><circle fill="#090909" fill-rule="nonzero" cx="14" cy="14" r="9"/><g fill="#FFF"><path d="m17.445 17.178 4.37 4.47-.715.699-2.208-2.259-1.413.831-1.443-.847-.694.718-.519-.902 2.622-2.71Zm.004 1.434-.698.72.728.427.693-.407-.723-.74Z"/><path d="m20.486 8-2.399 3.001L20.486 14h-8.54v.877l3.945 6.87-.867.497-2.157-3.754-1.388 1.39-1.429-1.429-2.182 3.798L7 21.751l3.946-6.87V8h9.54Zm-9.04 8.017-.881 1.535.914.914.873-.874-.906-1.575ZM18.406 9h-6.46v4h6.46l-1.601-1.999L18.406 9Z"/></g></g></svg>';
                                        }
                                        break;
                                    default:
                                        break;
                                }

                                return new atlas.HtmlMarker({
                                    position: position,
                                    color: color,
                                    htmlContent: htmlContent
                                });
                            }
                        }
                    });

                    //Add a click event to the marker layer.
                    vm.map.events.add('click', markerLayer, vm.markerClicked);

                    vm.map.events.add('mouseenter', markerLayer, vm.showPopup);

                    vm.map.events.add('mouseleave', markerLayer, vm.hidePopup);

                    //Disable one-finger scrolling the map on touch devices
                    if (window.ontouchstart !== undefined) {
                        vm.map.setUserInteraction({ dragPanInteraction: false, dblClickZoomInteraction: false });
                        vm.map.events.add('touchstart', function (e) {
                            if (e.originalEvent.touches.length === 2) {
                                vm.disableMapInteraction = false;
                                vm.map.setUserInteraction({ dragPanInteraction: true, dblClickZoomInteraction: true });
                            } else {
                                vm.disableMapInteraction = true;
                                vm.map.setUserInteraction({ dblClickZoomInteraction: false });
                            }
                        });
                        vm.map.events.add('dragstart', function (e) {
                            if (e.originalEvent.touches.length === 1) {
                                vm.disableMapInteraction = true;
                                vm.map.setUserInteraction({ dragPanInteraction: false, dblClickZoomInteraction: false });
                            } else {
                                vm.map.setUserInteraction({ dragPanInteraction: true, dblClickZoomInteraction: true });
                                vm.disableMapInteraction = false;
                            }
                        });
                        vm.map.events.add('dragend', function (e) {
                            vm.map.setUserInteraction({ dragPanInteraction: false, dblClickZoomInteraction: false });
                        });
                        vm.map.events.add('touchend', function (e) {
                                vm.disableMapInteraction = false;
                            })
                    }
                    
                    //Add marker layer to the map.
                    vm.map.layers.add(markerLayer);
                    vm.updateData();
                });
                this.mapInitialized = true;
            },
        beforeUnmount() {
            try {
                if (this.map) {
                    // remove map and listeners if supported
                    if (this.map.events && this.map.events.remove) {
                        // best-effort: atlas SDK usually exposes a dispose method
                    }
                    if (this.map.dispose) this.map.dispose();
                    this.map = null;
                }
            } catch (e) {
                // ignore cleanup errors
            }
        },
            updateData: function () {
                this.data = [];
                this.coordinates = [];
                this.markers.forEach((item) => {
                    if (item.longitude !== 0 && item.latitude !== 0) {
                        this.data.push(new atlas.data.Feature(new atlas.data.Point([
                            item.longitude,
                            item.latitude
                        ]), item));
                        this.coordinates.push([
                            item.longitude,
                            item.latitude
                        ]);
                    }
                });

                if (this.markers && this.datasource) {
                    this.calculateBoundingBox();
                }
                if (this.selectedMapItem) {
                    this.zoomToSingleMarker([this.selectedMapItem.longitude, this.selectedMapItem.latitude]);
                }
            },
            calculateBoundingBox: function () {
                this.datasource.setShapes(this.data);
                // Calculate bounding box around all markers to set position for
                if (this.coordinates.length > 1) {
                    const bounds = atlas.data.BoundingBox.fromPositions(this.coordinates);
                    this.map.setCamera({
                        bounds: bounds,
                        padding: 50,
                        //zoom: this.map.getCamera().zoom - 1
                    });
                }
                // only one marker to zoom on
                else if (this.coordinates.length === 1) {
                    this.zoomToSingleMarker(this.coordinates[0]);
                }
            },
            markerClicked: function(e) {
                var marker = e.target;
                if (marker.properties.cluster) {
                    //Get the cluster expansion zoom level. This is the zoom level at which the cluster starts to break apart.
                    this.datasource.getClusterExpansionZoom(marker.properties.cluster_id).then((zoom) => {

                        //Update the map camera to be centered over the cluster.
                        this.map.setCamera({
                            center: marker.getOptions().position,
                            zoom: zoom,
                            type: 'ease',
                            duration: 200
                        });
                    });
                } else {
                    this.$emit('map_point_selection', marker.properties.id);
                }
            },
            showPopup: function (e) {
                this.map.getCanvas().style.cursor = 'pointer';
                var marker = e.target;
                if (!marker.properties.cluster) {
                    var prop = marker.properties;
                    var desc = `<div class="contacts-block__map--popup"><h3>${prop.name}, ${prop.city}</h3></div>`;
                    this.popup.setOptions({
                        content: desc,
                        position: marker.getOptions().position
                    });
                    this.popup.open(this.map);
                }
            },
            hidePopup: function (e) {
                this.map.getCanvas().style.cursor = '';
                if (!e.target.properties.cluster) {
                    this.popup.close(this.map);
                }
            },
            tooltipCallback: function(marker, sliceIdx) {
                return `${this.companyTypes[sliceIdx]}: ${marker.getSliceValue(sliceIdx)}`;
            },
            zoomToSingleMarker: function (coords) {
                this.map.setCamera({
                    center: coords,
                    zoom: this.singleMarkerZoom,
                    type: 'ease',
                    duration: 1000
                });
            }
        }
    }
</script>